import {
  ApolloError,
  InternalRefetchQueriesInclude,
  useApolloClient,
} from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import {
  Chat,
  ListMessagesDocument,
  ProjectDocument,
  useDeleteProjectFilesMutation,
} from '@graphql/generated';

const NOT_FOUND_MESSAGE = 'could not be found';

export const useDeleteProjectDocument = () => {
  const apolloClient = useApolloClient();
  const [deleteProjectFile] = useDeleteProjectFilesMutation();
  const toast = useToast();
  const { t } = useTranslation('models');

  const deleteProjectFileById = (
    document: ProjectDocument[],
    chatId: Chat['id'],
    onComplete?: () => void,
    onError?: () => void
  ) => {
    const docids = document?.map((doc) => doc.id);
    const refetchList:
      | InternalRefetchQueriesInclude
      | (() => InternalRefetchQueriesInclude)
      | undefined = ['listProjectDocuments', 'listTaskDocuments'];
    if (Platform.OS !== 'web') {
      refetchList.push('getProject');
      if (chatId) {
        refetchList.push({
          query: ListMessagesDocument,
          variables: { chatId: chatId },
        });
      }
    }
    deleteProjectFile({
      variables: { ids: docids },
      // Override the global errorPolicy: 'all' so GraphQL errors route to
      // onError (not onCompleted), enabling not-found vs genuine-failure branching.
      errorPolicy: 'none',
      onCompleted: onComplete,
      onError: (error: ApolloError) => {
        const isNotFound = error.graphQLErrors?.some((e) =>
          e.message.toLowerCase().includes(NOT_FOUND_MESSAGE)
        );
        if (isNotFound) {
          // Documents already deleted server-side — evict locally and treat as success.
          // refetchQueries never ran (mutation errored), so trigger it manually so
          // listProjectDocuments and siblings don't show stale entries.
          document.forEach((doc) => {
            const cacheId = apolloClient.cache.identify(doc);
            if (cacheId) apolloClient.cache.evict({ id: cacheId });
          });
          apolloClient.cache.gc();
          apolloClient
            .refetchQueries({
              include: refetchList as InternalRefetchQueriesInclude,
            })
            .catch(() => {});
          onComplete?.();
        } else {
          if (onError) {
            onError();
          } else {
            toast.show(t('files.deleteFileFailed'));
          }
        }
      },
      refetchQueries: refetchList,
      update(cache, { data }) {
        if (!data) return;
        document.forEach((doc) => {
          const cacheIdentifier = cache.identify(doc);
          if (cacheIdentifier) cache.evict({ id: cacheIdentifier });
        });
        cache.gc();
      },
    });
  };

  return { deleteProjectFileById };
};
